import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { Button } from '../components/UI';
import SharedHeader from '../components/SharedHeader';
import SharedFooter from '../components/SharedFooter';

const ForTechnicians = () => {
  const { language, changeLanguage } = useLanguage();
  const t = (key) => getTranslation(language, key);

  useEffect(() => {
    // SEO Meta Tags
    const titles = {
      es: 'LIBERADO para Técnicos Veterinarios — Turnos Flexibles + Acceso Justo',
      en: 'Liberado for Veterinary Technicians — Flexible Shifts + Fair Access',
      pt: 'LIBERADO para Técnicos Veterinários — Turnos Flexíveis + Acesso Justo'
    };

    const descriptions = {
      es: 'Sistema impulsado por IA que brinda a los técnicos veterinarios en Chile mayor flexibilidad, ingresos transparentes y oportunidades de turnos verificadas.',
      en: 'AI-powered system giving veterinary technicians in Chile greater flexibility, transparent income, and verified shift opportunities.',
      pt: 'Sistema impulsionado por IA que oferece aos técnicos veterinários no Chile maior flexibilidade, renda transparente e oportunidades de turnos verificadas.'
    };

    document.title = titles[language] || titles.es;

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = descriptions[language] || descriptions.es;

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: titles[language] || titles.es },
      { property: 'og:description', content: descriptions[language] || descriptions.es },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://liberado.app/for-technicians' },
      { property: 'og:image', content: 'https://liberado.app/og-image.png' }
    ];

    ogTags.forEach(tag => {
      let ogTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', tag.property);
        document.head.appendChild(ogTag);
      }
      ogTag.content = tag.content;
    });
  }, [language]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
      <SharedHeader />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center text-yellow-400 hover:text-yellow-300 mb-8 transition">
            ← {language === 'es' ? 'Volver al inicio' : language === 'en' ? 'Back to home' : 'Voltar ao início'}
          </Link>

          {/* Title */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {language === 'es' ? 'Para Técnicos Veterinarios' : language === 'en' ? 'For Veterinary Technicians' : 'Para Técnicos Veterinários'}
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              {language === 'es' 
                ? 'Recupere el control de su carrera profesional con flexibilidad, transparencia y oportunidades basadas en habilidades verificadas'
                : language === 'en'
                ? 'Regain control of your professional career with flexibility, transparency, and opportunities based on verified skills'
                : 'Recupere o controle de sua carreira profissional com flexibilidade, transparência e oportunidades baseadas em habilidades verificadas'
              }
            </p>
          </div>

          {/* Features */}
          <div className="space-y-8">
            {/* Feature 1 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">🗓️</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Flexibilidad Profesional y Dignidad Laboral' : language === 'en' ? 'Professional Flexibility and Work Dignity' : 'Flexibilidade Profissional e Dignidade no Trabalho'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed">
                    {language === 'es'
                      ? 'Defina sus propios horarios y elija turnos que se alineen con su estilo de vida y metas profesionales. LIBERADO le permite equilibrar trabajo y vida personal sin comprometer su desarrollo de carrera. Acceda a oportunidades en sus propios términos, manteniendo autonomía y dignidad profesional.'
                      : language === 'en'
                      ? 'Define your own schedules and choose shifts that align with your lifestyle and professional goals. LIBERADO allows you to balance work and personal life without compromising your career development. Access opportunities on your own terms, maintaining autonomy and professional dignity.'
                      : 'Defina seus próprios horários e escolha turnos que se alinhem com seu estilo de vida e objetivos profissionais. A LIBERADO permite equilibrar trabalho e vida pessoal sem comprometer seu desenvolvimento de carreira. Acesse oportunidades em seus próprios termos, mantendo autonomia e dignidade profissional.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">⚖️</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Empoderamiento Profesional Basado en Habilidades' : language === 'en' ? 'Skills-Based Professional Empowerment' : 'Empoderamento Profissional Baseado em Habilidades'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed">
                    {language === 'es'
                      ? 'Nuestra futura tecnología de IA reconocerá y valorará sus habilidades específicas, experiencia y especialidades. Sea compensado justamente por su experiencia real, no solo por años de servicio. Acceda a oportunidades que verdaderamente coincidan con su nivel profesional y aspiraciones de crecimiento.'
                      : language === 'en'
                      ? 'Our future AI technology will recognize and value your specific skills, experience, and specialties. Be compensated fairly for your real expertise, not just years of service. Access opportunities that truly match your professional level and growth aspirations.'
                      : 'Nossa futura tecnologia de IA reconhecerá e valorizará suas habilidades específicas, experiência e especialidades. Seja compensado justamente por sua experiência real, não apenas por anos de serviço. Acesse oportunidades que realmente correspondam ao seu nível profissional e aspirações de crescimento.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">🔒</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Seguridad y Verificación Mutua' : language === 'en' ? 'Safety and Mutual Verification' : 'Segurança e Verificação Mútua'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed">
                    {language === 'es'
                      ? 'Trabaje únicamente con clínicas validadas y confiables que cumplan estándares profesionales. Nuestra plataforma verifica tanto a técnicos como a clínicas, creando un ecosistema seguro y profesional. Proteja su reputación y bienestar trabajando en entornos verificados que valoran su experiencia.'
                      : language === 'en'
                      ? 'Work only with validated and reliable clinics that meet professional standards. Our platform verifies both technicians and clinics, creating a safe and professional ecosystem. Protect your reputation and well-being by working in verified environments that value your expertise.'
                      : 'Trabalhe apenas com clínicas validadas e confiáveis que atendam aos padrões profissionais. Nossa plataforma verifica tanto técnicos quanto clínicas, criando um ecossistema seguro e profissional. Proteja sua reputação e bem-estar trabalhando em ambientes verificados que valorizam sua experiência.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">🚀</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Visibilidad y Acceso a Oportunidades (En Desarrollo)' : language === 'en' ? 'Opportunity Visibility and Access (In Development)' : 'Visibilidade e Acesso a Oportunidades (Em Desenvolvimento)'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed">
                    {language === 'es'
                      ? 'Nuestro futuro motor de IA le notificará sobre turnos que se ajustan perfectamente a su perfil, especialidades y preferencias. Nunca pierda oportunidades valiosas: reciba alertas inteligentes sobre proyectos que maximicen su potencial de ingresos y desarrollo profesional, dándole ventaja competitiva en el mercado.'
                      : language === 'en'
                      ? 'Our future AI engine will notify you about shifts that perfectly match your profile, specialties, and preferences. Never miss valuable opportunities: receive smart alerts about projects that maximize your income potential and professional development, giving you a competitive edge in the market.'
                      : 'Nosso futuro mecanismo de IA notificará você sobre turnos que correspondem perfeitamente ao seu perfil, especialidades e preferências. Nunca perca oportunidades valiosas: receba alertas inteligentes sobre projetos que maximizem seu potencial de renda e desenvolvimento profissional, dando-lhe vantagem competitiva no mercado.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 5 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">💰</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Transparencia en Ingresos y Protección de Pagos' : language === 'en' ? 'Income Transparency and Payment Protection' : 'Transparência de Renda e Proteção de Pagamentos'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed">
                    {language === 'es'
                      ? 'Conozca exactamente sus ingresos antes de aceptar cualquier turno. Nuestro futuro sistema de depósito en garantía (escrow) protegerá sus pagos desde el momento de la confirmación. Reciba compensación justa, puntual y segura por su trabajo profesional, eliminando incertidumbre financiera y asegurando estabilidad económica.'
                      : language === 'en'
                      ? 'Know exactly your earnings before accepting any shift. Our future escrow system will protect your payments from the moment of confirmation. Receive fair, timely, and secure compensation for your professional work, eliminating financial uncertainty and ensuring economic stability.'
                      : 'Saiba exatamente seus ganhos antes de aceitar qualquer turno. Nosso futuro sistema de depósito em garantia protegerá seus pagamentos desde o momento da confirmação. Receba compensação justa, pontual e segura por seu trabalho profissional, eliminando incerteza financeira e garantindo estabilidade econômica.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* AI Disclaimer */}
          <div className="mt-12 bg-yellow-500 bg-opacity-10 border border-yellow-500 rounded-lg p-6">
            <p className="text-sm text-yellow-200">
              {language === 'es'
                ? '⚠️ Algunas funciones de inteligencia artificial aún están en desarrollo y se implementarán progresivamente durante el piloto en Chile.'
                : language === 'en'
                ? '⚠️ Some artificial intelligence features are still in development and will be implemented progressively during the Chile pilot.'
                : '⚠️ Algumas funcionalidades de inteligência artificial ainda estão em desenvolvimento e serão implementadas progressivamente durante o piloto no Chile.'
              }
            </p>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center bg-yellow-500 bg-opacity-10 border-2 border-yellow-500 rounded-lg p-8">
            <h2 className="text-3xl font-bold mb-4">
              {language === 'es' ? '¿Listo para tomar el control de su carrera?' : language === 'en' ? 'Ready to take control of your career?' : 'Pronto para assumir o controle de sua carreira?'}
            </h2>
            <p className="text-lg text-gray-200 mb-6">
              {language === 'es'
                ? 'Únase al piloto de Chile y sea parte de la transformación del mercado veterinario'
                : language === 'en'
                ? 'Join the Chile pilot and be part of the veterinary market transformation'
                : 'Junte-se ao piloto do Chile e faça parte da transformação do mercado veterinário'
              }
            </p>
            <Link to="/interes">
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-black text-lg px-8 py-4 font-bold shadow-xl">
                {t('registerForInvitation')} →
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <SharedFooter />
    </div>
  );
};

export default ForTechnicians;
